/**
 * The safe selector grammar of the click trigger (`visitor_clicks_element`), the twin of backend
 * `app/services/workflows/selectors.py`; `contracts/workflow-selector-cases.json` holds both to the same answers.
 *
 *   list      := selector ("," selector){0,4}
 *   selector  := compound ((" " | " > ") compound)*     descendant and child combinators only
 *   compound  := tag? ("#id" | ".class" | "[attr]" | "[attr=value]")*
 *   value     := bare word | "quoted text" | 'quoted text'     (no commas, quotes, backslashes or control characters)
 *
 * No pseudo-classes (`:has()`, `:nth-*`), no sibling combinators, no universal selector, no substring attribute
 * matchers, no escapes; at most 200 characters in all. The result is the canonical text of each selector (single
 * spaces, attribute values double-quoted), which is what the public widget config carries and the loader matches.
 */
import { SELECTOR_LIMITS } from "./workflow-triggers";

export type SelectorError = "empty" | "too_long" | "too_many" | "unsupported" | "syntax";

export type ParsedSelectors = { selectors: string[]; error: null } | { selectors: null; error: SelectorError };

const WHITESPACE = " \t\r\n\f";
/** A character that begins something CSS has and this grammar does not: seen where the grammar fails, it is "unsupported". */
const UNSUPPORTED = ":*+~|^$()\\@/;{}";
const TAG = new RegExp(`[A-Za-z][A-Za-z0-9-]{0,${String(SELECTOR_LIMITS.tag - 1)}}`, "y");
const IDENT = new RegExp(`[A-Za-z_][A-Za-z0-9_-]{0,${String(SELECTOR_LIMITS.ident - 1)}}`, "y");
const BARE_VALUE = new RegExp(`[A-Za-z0-9_-]{1,${String(SELECTOR_LIMITS.value)}}`, "y");
const QUOTED_TEXT = `[^"'\\\\,\\x00-\\x1f]{0,${String(SELECTOR_LIMITS.value)}}`;
const QUOTED_VALUE = new RegExp(`"(${QUOTED_TEXT})"|'(${QUOTED_TEXT})'`, "y");

class Refused extends Error {
  constructor(readonly code: SelectorError) {
    super(code);
  }
}

class Scanner {
  pos = 0;

  constructor(private readonly text: string) {}

  get atEnd(): boolean {
    return this.pos >= this.text.length;
  }

  peek(): string {
    return this.atEnd ? "" : (this.text[this.pos] ?? "");
  }

  refuse(): Refused {
    return new Refused(!this.atEnd && UNSUPPORTED.includes(this.peek()) ? "unsupported" : "syntax");
  }

  skipSpace(): boolean {
    const start = this.pos;
    while (!this.atEnd && WHITESPACE.includes(this.peek())) this.pos += 1;
    return this.pos > start;
  }

  take(pattern: RegExp): string {
    pattern.lastIndex = this.pos;
    const found = pattern.exec(this.text);
    if (found === null) throw this.refuse();
    this.pos = pattern.lastIndex;
    return found[0];
  }

  value(): string {
    QUOTED_VALUE.lastIndex = this.pos;
    const quoted = QUOTED_VALUE.exec(this.text);
    if (quoted !== null) {
      this.pos = QUOTED_VALUE.lastIndex;
      return quoted[1] ?? quoted[2] ?? "";
    }
    return this.take(BARE_VALUE);
  }

  attribute(): string {
    this.pos += 1;
    this.skipSpace();
    const name = this.take(IDENT);
    this.skipSpace();
    if (this.peek() === "]") {
      this.pos += 1;
      return `[${name}]`;
    }
    if (this.peek() !== "=") throw this.refuse();
    this.pos += 1;
    this.skipSpace();
    const value = this.value();
    this.skipSpace();
    if (this.peek() !== "]") throw this.refuse();
    this.pos += 1;
    return `[${name}="${value}"]`;
  }

  compound(): string {
    const parts: string[] = [];
    TAG.lastIndex = this.pos;
    const tag = TAG.exec(this.text);
    if (tag !== null) {
      parts.push(tag[0]);
      this.pos = TAG.lastIndex;
    }
    for (;;) {
      const marker = this.peek();
      if (marker === "[") {
        parts.push(this.attribute());
      } else if (marker === "#" || marker === ".") {
        this.pos += 1;
        parts.push(marker + this.take(IDENT));
      } else break;
    }
    if (parts.length === 0) throw this.refuse();
    return parts.join("");
  }

  selector(): string {
    const parts = [this.compound()];
    for (;;) {
      const spaced = this.skipSpace();
      if (this.atEnd || this.peek() === ",") return parts.join(" ");
      if (this.peek() === ">") {
        this.pos += 1;
        this.skipSpace();
        parts.push(">", this.compound());
      } else if (spaced) parts.push(this.compound());
      else throw this.refuse();
    }
  }

  selectorList(): string[] {
    const found: string[] = [];
    for (;;) {
      this.skipSpace();
      found.push(this.selector());
      this.skipSpace();
      if (this.atEnd) return found;
      if (this.peek() !== ",") throw this.refuse();
      this.pos += 1;
    }
  }
}

function trimmed(text: string): string {
  let start = 0;
  let end = text.length;
  while (start < end && WHITESPACE.includes(text[start] ?? "")) start += 1;
  while (end > start && WHITESPACE.includes(text[end - 1] ?? "")) end -= 1;
  return text.slice(start, end);
}

/** The canonical selectors of a comma-separated list, or the reason it is refused. */
export function parseSelectors(text: string): ParsedSelectors {
  const cleaned = trimmed(text);
  if (cleaned === "") return { selectors: null, error: "empty" };
  if (cleaned.length > SELECTOR_LIMITS.chars) return { selectors: null, error: "too_long" };
  let found: string[];
  try {
    found = new Scanner(cleaned).selectorList();
  } catch (error) {
    if (error instanceof Refused) return { selectors: null, error: error.code };
    throw error;
  }
  if (found.length > SELECTOR_LIMITS.selectors) return { selectors: null, error: "too_many" };
  return { selectors: found, error: null };
}
